// src/components/Header.js
import { useState } from "react";
import { useAuth } from "../AuthContext";

export default function Header({ currentView, setView, setEditingTask, defaultTask, taskCounts }) {
  const { user, logout } = useAuth();
  const [showMenu, setShowMenu] = useState(false);
  const [showMobileNav, setShowMobileNav] = useState(false);

  const navItems = [
    { id: "dashboard", label: "Dashboard" },
    { id: "tasks",     label: "Tasks" },
    { id: "add",       label: "New Task" },
    { id: "calendar",  label: "Schedule" },
    { id: "household", label: "Household" },
    { id: "settings",  label: "Settings" },
  ];

  function handleNav(id) {
    setView(id);
    if (id === "add") setEditingTask({ ...defaultTask });
    setShowMobileNav(false);
  }

  return (
    <>
      <header style={styles.header}>
        <div style={styles.logo} onClick={() => setView("dashboard")} role="button">
          <img src={process.env.PUBLIC_URL + "/logo.png"} alt="Homie" style={styles.logoImg} />
          <span style={styles.logoText}>Homie Fix It</span>
        </div>

        {/* Desktop Nav */}
        <nav style={styles.desktopNav}>
          {navItems.map(({ id, label }) => (
            <button
              key={id}
              onClick={() => handleNav(id)}
              style={{
                ...styles.navBtn,
                ...(currentView === id || (currentView === "detail" && id === "tasks") || (currentView === "edit" && id === "tasks") ? styles.navBtnActive : {}),
              }}
            >
              {label}
              {id === "tasks" && taskCounts.open > 0 && (
                <span style={styles.badge}>{taskCounts.open}</span>
              )}
            </button>
          ))}
        </nav>

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {/* User menu */}
          <div style={{ position: "relative" }}>
            <button style={styles.userBtn} onClick={() => setShowMenu(v => !v)}>
              {user?.photoURL
                ? <img src={user.photoURL} alt="" style={styles.avatar} referrerPolicy="no-referrer" />
                : <div style={styles.avatarFallback}>{(user?.displayName || user?.email || "?")[0].toUpperCase()}</div>
              }
              <span style={styles.userName}>{user?.displayName || user?.email?.split("@")[0]}</span>
              <span style={{ fontSize: 12, color: "#f9cc90", marginLeft: 4 }}>▾</span>
            </button>

            {showMenu && (
              <div style={styles.dropdown} onClick={() => setShowMenu(false)}>
                <div style={styles.dropdownHeader}>
                  <div style={{ fontWeight: 800, color: "#2c1810", fontSize: 14 }}>{user?.displayName || "User"}</div>
                  <div style={{ fontSize: 12, color: "#a07850", marginTop: 2 }}>{user?.email}</div>
                </div>
                <div style={styles.dropdownDivider} />
                <div style={{ padding: "4px 8px" }}>
                  <button style={styles.dropdownItem} onClick={() => { setView("settings"); setShowMenu(false); }}>Settings</button>
                  <button style={styles.dropdownItem} onClick={logout}>Sign Out</button>
                </div>
              </div>
            )}
          </div>

          {/* Hamburger - mobile only */}
          <button style={styles.hamburger} onClick={() => setShowMobileNav(v => !v)} aria-label="Menu">
            <div style={styles.hamburgerLine} />
            <div style={styles.hamburgerLine} />
            <div style={styles.hamburgerLine} />
          </button>
        </div>
      </header>

      {/* Mobile Drawer */}
      {showMobileNav && (
        <>
          <div style={styles.backdrop} onClick={() => setShowMobileNav(false)} />
          <div style={styles.drawer}>
            <div style={styles.drawerHeader}>
              <img src={process.env.PUBLIC_URL + "/logo.png"} alt="Homie" style={{ width: 36, height: 36, objectFit: "contain" }} />
              <span style={{ fontFamily: "'Fredoka One', cursive", fontSize: 22, color: "#8B4513", marginLeft: 10 }}>Homie Fix It</span>
            </div>
            {navItems.map(({ id, label }) => (
              <button key={id} onClick={() => handleNav(id)}
                style={{ ...styles.drawerItem, ...(currentView === id ? styles.drawerItemActive : {}) }}>
                {label}
                {id === "tasks" && taskCounts.open > 0 && (
                  <span style={{ ...styles.badge, marginLeft: 8 }}>{taskCounts.open}</span>
                )}
              </button>
            ))}
            <div style={{ borderTop: "1px solid #f0d8b0", marginTop: 8, paddingTop: 8 }}>
              <button style={{ ...styles.drawerItem, color: "#dc2626" }} onClick={() => { logout(); setShowMobileNav(false); }}>
                Sign Out
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}

const styles = {
  header: {
    background: "linear-gradient(135deg, #8B4513 0%, #A0522D 60%, #CD853F 100%)",
    borderBottom: "4px solid #f97316", padding: "0 16px", height: 64,
    display: "flex", alignItems: "center", gap: 16,
    boxShadow: "0 3px 16px rgba(139,69,19,0.28)",
    position: "sticky", top: 0, zIndex: 100, flexShrink: 0,
  },
  logo: { display: "flex", alignItems: "center", gap: 10, flexShrink: 0, cursor: "pointer" },
  logoImg: { height: 38, width: 38, objectFit: "contain" },
  logoText: { fontFamily: "'Fredoka One', cursive", fontSize: 20, color: "white" },
  desktopNav: { display: "flex", gap: 2, flex: 1 },
  navBtn: {
    background: "rgba(255,255,255,0.0)", border: "none", color: "rgba(255,255,255,0.75)",
    padding: "8px 12px", borderRadius: 10, cursor: "pointer",
    fontFamily: "'Nunito', sans-serif", fontSize: 13, fontWeight: 700,
    display: "flex", alignItems: "center", transition: "all 0.18s",
  },
  navBtnActive: { background: "rgba(255,255,255,0.22)", color: "white", boxShadow: "0 2px 8px rgba(0,0,0,0.15)" },
  badge: { background: "#ef4444", color: "white", borderRadius: 20, fontSize: 11, fontWeight: 900, padding: "1px 6px", marginLeft: 6 },
  userBtn: {
    background: "rgba(255,255,255,0.15)", border: "2px solid rgba(255,255,255,0.3)",
    borderRadius: 12, padding: "6px 10px", cursor: "pointer",
    display: "flex", alignItems: "center", gap: 8, fontFamily: "'Nunito', sans-serif",
  },
  avatar: { width: 30, height: 30, borderRadius: "50%", border: "2px solid white" },
  avatarFallback: { width: 30, height: 30, borderRadius: "50%", background: "#f97316", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, fontSize: 14, flexShrink: 0 },
  userName: { color: "white", fontSize: 13, fontWeight: 700, maxWidth: 80, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  dropdown: { position: "absolute", top: "calc(100% + 10px)", right: 0, background: "white", borderRadius: 16, boxShadow: "0 8px 32px rgba(139,69,19,0.2)", border: "1px solid #f0d8b0", minWidth: 220, zIndex: 200, overflow: "hidden" },
  dropdownHeader: { padding: "16px 16px 12px" },
  dropdownDivider: { height: 1, background: "#f0d8b0", margin: "0 8px" },
  dropdownItem: { width: "100%", background: "none", border: "none", textAlign: "left", padding: "10px 12px", borderRadius: 10, cursor: "pointer", fontFamily: "'Nunito', sans-serif", fontSize: 14, fontWeight: 700, color: "#5c3a1e", display: "block" },
  hamburger: { display: "none", flexDirection: "column", gap: 5, background: "rgba(255,255,255,0.15)", border: "2px solid rgba(255,255,255,0.3)", borderRadius: 10, padding: "8px 10px", cursor: "pointer" },
  hamburgerLine: { width: 20, height: 2, background: "white", borderRadius: 2 },
  backdrop: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 150 },
  drawer: { position: "fixed", top: 0, right: 0, bottom: 0, width: 280, background: "white", zIndex: 200, boxShadow: "-4px 0 24px rgba(0,0,0,0.2)", padding: "20px 12px", overflowY: "auto", animation: "slideIn 0.25s ease" },
  drawerHeader: { display: "flex", alignItems: "center", padding: "8px 12px 20px", borderBottom: "2px solid #f5ead8", marginBottom: 8 },
  drawerItem: { width: "100%", background: "none", border: "none", textAlign: "left", padding: "14px 16px", borderRadius: 12, cursor: "pointer", fontFamily: "'Nunito', sans-serif", fontSize: 16, fontWeight: 700, color: "#5c3a1e", display: "flex", alignItems: "center", transition: "background 0.15s", marginBottom: 2 },
  drawerItemActive: { background: "#fff7ed", color: "#f97316" },
};
